import { inject, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { PermissionService } from '@ipg/ngx-frontend-toolset';
import { combineLatest, filter, map, Observable, take } from 'rxjs';
import { UnitService } from '../views/units/services/unit.service';

/**
 * A route the root chooser may send a user to, and what it takes to get there.
 *
 * `rootUnitOnly` mirrors `canActivateRootUnitGuard` on the route itself, and
 * `nonRootUnitOnly` its counterpart `canActivateNonRootUnitGuard`. The chooser
 * has to know about them: offering a page whose own guard refuses the user
 * bounces them to '/settings', and from there back here — the redirect loop
 * this guard exists to terminate.
 */
type RouteCandidate = {
  permission: string;
  path: string;
  rootUnitOnly?: boolean;
  nonRootUnitOnly?: boolean;
};

/** The settings pages, in the order of the settings menu. */
const SETTINGS_CANDIDATES: RouteCandidate[] = [
  { permission: 'unit:read', path: 'settings/general' },
  { permission: 'user:read', path: 'settings/users' },
  { permission: 'user_role:read', path: 'settings/roles' },
  { permission: 'contact:read', path: 'settings/contacts' },
  { permission: 'var:read', path: 'settings/variables', rootUnitOnly: true },
  { permission: 'tag:read', path: 'settings/tags', rootUnitOnly: true },
  { permission: 'support_session:read', path: 'settings/support-sessions' },
  {
    permission: 'ai_agent_settings:read',
    path: 'settings/ai-agents/creator',
    rootUnitOnly: true,
  },
  { permission: 'audit_log:read', path: 'settings/audit-logs', rootUnitOnly: true },
  {
    permission: 'notification_log:read',
    path: 'settings/notification-logs',
    rootUnitOnly: true,
  },
  { permission: 'unit:move', path: 'settings/move', nonRootUnitOnly: true },
];

/**
 * Every permission that opens a settings page. The sidenav shows Settings for
 * any of them; without one, '/settings' has nothing to offer.
 */
export const SETTINGS_PERMISSIONS: string[] = SETTINGS_CANDIDATES.map(
  (candidate) => candidate.permission
);

const ROUTE_CANDIDATES: RouteCandidate[] = [
  { permission: 'client:read', path: 'clients' },
  { permission: 'device:read', path: 'devices' },
  { permission: 'interaction:read', path: 'templates' },
  { permission: 'media:read', path: 'media' },
  { permission: 'download:read', path: 'downloads' },
  ...SETTINGS_CANDIDATES,
];

/**
 * Default redirect guard for the root path.
 *
 * Checks permissions in priority order and redirects to the first
 * route the user has access to. Always returns a UrlTree (redirect),
 * so the associated component never renders.
 *
 * Usage in routes:
 *   canActivate: [defaultRedirectGuard]
 */
export const defaultRedirectGuard: CanActivateFn = () =>
  /*
    Must end on a route with no permissionGuard on it. Anything gated
    redirects back to the root on denial, which lands here again, and the
    two bounce off each other until the tab locks up.

    '/settings' used to play that part precisely because it was ungated,
    which is what left the settings pages readable by a user with no role
    at all. Its children are guarded now, so the fallback has to be a page
    that grants nothing.
  */
  redirectToFirstAllowed(ROUTE_CANDIDATES, '/no-access');

/**
 * Redirect guard for '/settings' itself: the first settings page the user may
 * open, instead of always 'general', which needs `unit:read` and bounced
 * everyone without it to the root.
 *
 * Falls back to the root, never to a settings page: the root's chooser offers
 * every settings page as well and ends on '/no-access', so the two cannot
 * bounce off each other.
 */
export const settingsRedirectGuard: CanActivateFn = () =>
  redirectToFirstAllowed(SETTINGS_CANDIDATES, '/');

/**
 * Redirects to the first of `candidates` the user may open, or to `fallback`.
 * Must run in an injection context, as guards do.
 */
function redirectToFirstAllowed(
  candidates: RouteCandidate[],
  fallback: string
): Observable<UrlTree> {
  const permissionService = inject(PermissionService);
  const unitService = inject(UnitService);
  const router = inject(Router);
  const injector = inject(Injector);

  /*
    Permissions arrive with the user fetch, which is kicked off by the auth
    state change — authGuard does not wait for it, and the login form routes
    here the moment the credentials are accepted. Deciding before the fetch
    lands would read an empty permission set for every user and send even a
    full administrator to /no-access, so settle first and choose after. The
    unit comes from the same fetch and decides the `rootUnitOnly` and
    `nonRootUnitOnly` candidates.
  */
  return combineLatest([
    toObservable(permissionService.isLoaded, { injector }),
    toObservable(unitService.currentUnit, { injector }),
  ]).pipe(
    filter(
      ([isLoaded, unit]) => isLoaded && unit !== null
    ),
    take(1),
    map(([, unit]) => {
      const isRootUnit = unit!.prevUnitId === null;

      for (const candidate of candidates) {
        if (candidate.rootUnitOnly && !isRootUnit) continue;
        if (candidate.nonRootUnitOnly && isRootUnit) continue;
        if (permissionService.hasPermission(candidate.permission)) {
          return router.createUrlTree([candidate.path]);
        }
      }

      return router.createUrlTree([fallback]);
    }),
  );
}

/**
 * Allows a route for any user holding at least one permission.
 *
 * For routes that are not about a particular permission but should still not
 * be the one thing a user with no role at all can open — `/docs`, whose manual
 * only describes pages such a user cannot reach.
 *
 * Waits on `isLoaded` for the same reason the chooser above does: the
 * permission set is empty until the user fetch lands, so deciding earlier would
 * turn every full administrator away on their first navigation.
 */
export const anyPermissionGuard: CanActivateFn = (): Observable<
  boolean | UrlTree
> => {
  const permissionService = inject(PermissionService);
  const router = inject(Router);
  const injector = inject(Injector);

  return toObservable(permissionService.isLoaded, { injector }).pipe(
    filter((isLoaded) => isLoaded),
    take(1),
    map(() =>
      permissionService.permissions().size > 0
        ? true
        : router.createUrlTree(['/no-access']),
    ),
  );
};
